import {ref, computed, watch} from 'vue';
import _ from 'lodash';
import {useValidation} from "@/composables/useValidation";
import {useAuthService} from "@/composables/useAuthService";

export function useFixtureForm(initProps) {
  const inputProps = ref(initProps);
  const props = computed(() => inputProps.value ?? initProps);
  const { t } = useI18n();
  const {isPlayerInFixture} = useValidation();
  const {notify} = useListenerService();
  const {user, isAdminOfClub, isAuthenticated, isSuperUser} = useAuthService();
  const router = useRouter();
  const route = useRoute();

  const scoringSystem = ref(null);
  const gameData = ref([]);
  const homeResult = ref(null);
  const awayResult = ref(null);
  const homeGames = ref(0);
  const awayGames = ref(0);

  const homeTeamId = computed(() => props.value.homeTeamId ?? fixture.value?.homeTeamId);
  const awayTeamId = computed(() => props.value.awayTeamId ?? fixture.value?.awayTeamId);

  const isAdmin = computed(() => isAuthenticated.value && fixture.value && (isSuperUser.value || isAdminOfClub(fixture.value.clubId)));

  const isMyFixture = computed(() => isAuthenticated.value && fixture.value && isPlayerInFixture(user.value.playerId, fixture.value));

  const formType = computed(() => props.value.fixtureId != null ? (isAdmin.value || needsMyApproval.value || isMyFixture.value ? "edit" : "view") : "add");

  const canEdit = computed(() => {
    if (!fixture.value) return true;
    return isAdmin.value ? fixture.value.adminEditable : (isMyFixture.value && fixture.value.playerEditable);
  });

  const canCreate = computed(() => isAuthenticated.value && props.value.fixtureId == null);

  const needsMyApproval = computed(() => {
    if (!isAuthenticated.value || !fixture.value || !fixture.awayTeam || !fixture.homeTeam) return false;
    const {awayTeam, homeTeam} = fixture.value;
    const playerId = user.value.playerId;
    return (awayTeam.players.some(p => p.id === playerId) && !fixture.value.awayTeamApprovedAt) ||
        (homeTeam.players.some(p => p.id === playerId) && !fixture.value.homeTeamApprovedAt);
  });

  const title = computed(() => {
    if (isAdmin.value && canEdit.value && fixture.value?.approvedAt) {
      return t('edit_fixture_admin');
    } else if (isMyFixture.value && canEdit.value && fixture.value?.approvedAt) {
      return t('edit_fixture');
    }
    return props.value.fixtureId ? (canEdit.value ? t('view_fixtures', 0) : (needsMyApproval.value ? t('approve_fixture') : t('edit_fixture'))) : t('create_fixture');
  });

  const linkerNamespace = computed(() => {
    if (props.value.leagueCycleDivisionId != null) return "leagueCycleDivisions";
    if (props.value.ladderChallengeId != null) return "ladderChallenges";
    return null;
  });

  const linkerId = computed(() => {
    if (props.value.leagueCycleDivisionId != null) return props.value.leagueCycleDivisionId;
    if (props.value.ladderChallengeId != null) return props.value.ladderChallengeId;
    return null;
  });

  const noEditReason = computed(() => {
    if (!fixture.value) return null;
    if (isAdmin.value) return fixture.value.adminNonEditableReason;
    if (isMyFixture.value) return fixture.value.playerNonEditableReason;
    return null;
  });

  const buttonText = computed(() => {
    if (isAdmin.value && isApproved.value) {
      return t('edit_fixture_admin');
    } else if (isMyFixture.value && isApproved.value) {
      return t('edit_fixture');
    } else if (props.value.fixtureId != null) {
      return isApproved.value
          ? "Update result"
          : (needsMyApproval.value || isAdmin.value)
              ? t('approve_fixture')
              : "Update result";
    } else {
      return "Enter result";
    }
  });

  const isApproved = computed(() => {
    return fixture.value != null && fixture.value.approvedAt != null;
  });

  const save = async () => {
    if (props.value.isDelete) {
      await deleteFixture(fixture.value.id);
    } else {
      await createFixture(prepareSaveData());
    }
    if (!hasFixtureErrors.value) {
      notify('fixtureUpdated', fixture.value);
    }
  };

  const prepareSaveData = () => {
    return {
      homeTeamId: homeTeamId.value,
      awayTeamId: awayTeamId.value,
      homeTeamResult: homeResult.value,
      awayTeamResult: awayResult.value,
      approve: true,
      scoringSystemId: scoringSystem.value.id,
      games: scoringSystem.value.splitGames ? gameData.value.filter(g => g.homeTeamScore != null && g.awayTeamScore != null).map((g, i) => ({
        homeTeamScore: _.parseInt(g.homeTeamScore),
        awayTeamScore: _.parseInt(g.awayTeamScore),
        sort: i
      })) : null,
      leagueCycleDivisionId: props.value.leagueCycleDivisionId || fixture.value?.leagueCycleDivisionId,
      ladderChallengeId: props.value.ladderChallengeId || fixture.value?.ladderChallengeId
    };
  };

  const calculateScore = () => {
    if(gameData.value.length === 0) return;
    if(!scoringSystem.value.splitGames) return;
    homeGames.value = 0;
    awayGames.value = 0;
    gameData.value.forEach(game => {
      const home = parseInt(game.homeTeamScore);
      const away = parseInt(game.awayTeamScore);
      if (home > away) homeGames.value++;
      else if (away > home) awayGames.value++;
    });
    const outcome = scoringSystem.value.outcomes.find(o => o.homeGames === homeGames.value && o.awayGames === awayGames.value);
    homeResult.value = outcome?.homeResult || "-";
    awayResult.value = outcome?.awayResult || "-";
  };

  const onFixtureUpdated = () => {
    if (!fixture.value) return;
    homeTeam.value = fixture.value.homeTeam;
    awayTeam.value = fixture.value.awayTeam;
    const newScoringSystem = _.cloneDeep(fixture.value.scoringSystem);
    if (fixture.value.games.length === 0) newScoringSystem.splitGames = false;
    gameData.value = newScoringSystem.splitGames ? fixture.value.games : [];
    while (gameData.value.length < newScoringSystem.numberGames) gameData.value.push({});
    scoringSystem.value = newScoringSystem;
    homeResult.value = fixture.value.homeTeamResult;
    awayResult.value = fixture.value.awayTeamResult;
  };

  const onLinkerUpdated = async () => {
    if (!linker.value) return;
    gameData.value = [];
    homeResult.value = awayResult.value = null;
    if (props.value.leagueCycleDivisionId != null) {
      scoringSystem.value = {...linker.value.scoringSystem};
      let updatedFixture = fixture.value || {};
      updatedFixture.homeTeamId = props.value.homeTeamId;
      updatedFixture.awayTeamId = props.value.awayTeamId;
      await getHomeTeam(props.value.homeTeamId);
      await getAwayTeam(props.value.awayTeamId);
      updatedFixture.leagueCycleDivisionId = props.value.leagueCycleDivisionId;
      fixture.value = updatedFixture;
    }
    if (props.value.ladderChallengeId != null) {
      scoringSystem.value = {...linker.value.scoringSystem};
      let updatedFixture = fixture.value || {};
      updatedFixture.awayTeam = linker.value.challengedTeam;
      updatedFixture.awayTeamId = linker.value.challengedTeamId;
      updatedFixture.homeTeam = linker.value.challengingTeam;
      updatedFixture.homeTeamId = linker.value.challengingTeamId;
      homeTeam.value = linker.value.challengingTeam;
      awayTeam.value = linker.value.challengedTeam;
      updatedFixture.ladderChallengeId = props.value.ladderChallengeId;
      fixture.value = updatedFixture;
    }
  };

  // Watchers for dynamically updating UI
  watch(gameData, calculateScore, {deep: true});

  watch(scoringSystem, (newVal) => {
    if (!newVal || gameData.value.length > 0) return;
    gameData.value = Array.from({ length: newVal.numberGames }, () => ({ homeTeamScore: null, awayTeamScore: null }));
    if (!newVal.splitGames) homeResult.value = awayResult.value = null;
  });

  const {
    getItem: getFixture,
    item: fixture,
    formPending: fixtureSaveLoading,
    formErrors: fixtureErrors,
    createItem: createFixture,
    deleteItem: deleteFixture,
    hasFormErrors: hasFixtureErrors
  } = useCrudApi('api/v2/fixtures');

  const {
    getItem: getHomeTeam,
    item: homeTeam,
  } = useCrudApi('api/v2/teams');

  const {
    getItem: getAwayTeam,
    item: awayTeam,
  } = useCrudApi('api/v2/teams');

  const {
    getItem: getLeagueCycleDivision,
    item: leagueCycleDivision,
    itemPending: leagueCycleDivisionLoading,
    formErrors: leagueCycleDivisionErrors
  } = useCrudApi(`api/v2/leagueCycleDivisions`);

  const {
    getItem: getLadderChallenge,
    item: ladderChallenge,
    itemPending: ladderChallengeLoading,
    formErrors: ladderChallengeErrors
  } = useCrudApi(`api/v2/ladderChallenges`);

  const getLinker = async (id) => {
    if (linkerNamespace.value === "leagueCycleDivisions") {
      await getLeagueCycleDivision(id);
    } else if (linkerNamespace.value === "ladderChallenges") {
      await getLadderChallenge(id);
    }
  };

  const linker = computed(() => {
    if (linkerNamespace.value === "leagueCycleDivisions") return leagueCycleDivision.value;
    if (linkerNamespace.value === "ladderChallenges") return ladderChallenge.value;
    return null;
  });

  const linkerLoading = computed(() => {
    if (linkerNamespace.value === "leagueCycleDivisions") return leagueCycleDivisionLoading.value;
    if (linkerNamespace.value === "ladderChallenges") return ladderChallengeLoading.value;
    return false;
  });

  const linkerErrors = computed(() => {
    if (linkerNamespace.value === "leagueCycleDivisions") return leagueCycleDivisionErrors.value;
    if (linkerNamespace.value === "ladderChallenges") return ladderChallengeErrors.value;
    return null;
  });

  // Universal loadFixture with dynamic props
  const loading = ref(false);
  const loadFixture = async (p) => {
    fixture.value= {};
    inputProps.value = p;
    loading.value = true;
    if (props.value.fixtureId) {
      await getFixture(props.value.fixtureId);
      onFixtureUpdated();
    } else if (linkerId.value) {
      await getLinker(linkerId.value);
      await onLinkerUpdated();
    }
    loading.value = false;
  };

  return {
    loadFixture,
    homeTeam,
    awayTeam,
    scoringSystem,
    gameData,
    homeResult,
    awayResult,
    homeGames,
    awayGames,
    formType,
    isAdmin,
    isMyFixture,
    isApproved,
    canEdit,
    canCreate,
    fixture,
    needsMyApproval,
    title,
    loading,
    save,
    fixtureSaveLoading,
    fixtureErrors,
    noEditReason,
    buttonText,
    linker,
    linkerLoading,
    linkerErrors
  };
}

